/**
 * AdminCommunicationPage — Module 9 Communication Center.
 *
 * Three-panel inbox on desktop (list · thread · customer context) and a
 * push-nav mobile layout (list → thread → context). Admin/master_admin
 * only; the backend enforces the same rule with 403 for Guru accounts.
 *
 * Provider send is intentionally not built here (Section 8). Composer is
 * omitted; the empty-state footer surfaces the "provider not connected"
 * status so admins understand external send is coming later.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import InboxTabs from '../../components/admin/InboxTabs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  MessageSquare, Mail, Search, RefreshCw, Loader2, ChevronLeft,
  Phone, User, MapPin, ExternalLink, CheckCheck, Inbox, ShieldQuestion,
  ShieldCheck, ShieldAlert, CircleDot, Clock, Info, ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// Small utilities
// ============================================================================
const CHANNEL_META = {
  sms:   { label: 'SMS',   icon: MessageSquare, tint: 'text-[#F56F05]' },
  email: { label: 'Email', icon: Mail,          tint: 'text-blue-600'  },
  portal: { label: 'Client portal', icon: MessageSquare, tint: 'text-[#52623f]' },
  team: { label: 'Guru team', icon: MessageSquare, tint: 'text-[#52623f]' },
};
const STATUS_META = {
  open:   { label: 'Open',   dot: 'bg-emerald-500' },
  closed: { label: 'Closed', dot: 'bg-zinc-400' },
};
const STATE_META = {
  unknown:      { label: 'Unknown', icon: ShieldQuestion, tint: 'text-zinc-500' },
  opted_in:     { label: 'Opted In', icon: ShieldCheck, tint: 'text-emerald-600' },
  opted_out:    { label: 'Opted Out', icon: ShieldAlert, tint: 'text-red-600' },
  subscribed:   { label: 'Subscribed', icon: ShieldCheck, tint: 'text-emerald-600' },
  unsubscribed: { label: 'Unsubscribed', icon: ShieldAlert, tint: 'text-red-600' },
};

const initialsFrom = (name) => (name || 'GG').split(' ').filter(Boolean)
  .map(w => w[0]).slice(0, 2).join('').toUpperCase();

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.floor((now - then) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  if (s < 604800) return `${Math.floor(s/86400)}d`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-US',
      { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};

const fmtFullDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US',
      { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

// ============================================================================
// Inbox row
// ============================================================================
const InboxRow = ({ conv, selected, onSelect }) => {
  const ChanIcon = CHANNEL_META[conv.channel]?.icon || MessageSquare;
  const chanTint = CHANNEL_META[conv.channel]?.tint || 'text-zinc-500';
  const isUnread = (conv.unread_count || 0) > 0;
  const name = conv.customer_display?.name || conv.customer_display?.email || 'Unknown';
  const isLead = (conv.customer_id || '').startsWith('lead:');

  return (
    <button
      type="button"
      onClick={() => onSelect(conv)}
      data-testid={`inbox-row-${conv.id}`}
      className={`w-full text-left border-b border-zinc-100 px-4 py-3 flex gap-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F56F05] focus-visible:ring-inset ${
        selected ? 'bg-orange-50/70' : 'hover:bg-zinc-50'
      }`}
    >
      <div className="relative flex-shrink-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${
          selected ? 'bg-[#F56F05] text-white' : 'bg-zinc-100 text-zinc-600'
        }`}>
          {initialsFrom(name)}
        </div>
        {isUnread && (
          <span
            data-testid={`inbox-unread-dot-${conv.id}`}
            className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#F56F05] ring-2 ring-white"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className={`text-sm truncate ${isUnread ? 'font-bold text-zinc-900' : 'font-semibold text-zinc-800'}`}>
            {name}
            {isLead && (
              <span className="ml-1.5 text-[9px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded uppercase tracking-wide align-middle">Lead</span>
            )}
          </p>
          <span className="text-[10px] text-zinc-500 flex-shrink-0" title={fmtFullDate(conv.last_message_at)}>
            {timeAgo(conv.last_message_at)}
          </span>
        </div>
        <p className={`text-[12px] mt-0.5 truncate ${isUnread ? 'text-zinc-800' : 'text-zinc-500'}`}>
          {conv.last_message_preview || <span className="italic text-zinc-400">No messages yet.</span>}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500">
            <ChanIcon className={`w-3 h-3 ${chanTint}`} />
            {CHANNEL_META[conv.channel]?.label || conv.channel}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500">
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_META[conv.status]?.dot || 'bg-zinc-300'}`} />
            {STATUS_META[conv.status]?.label || conv.status}
          </span>
        </div>
      </div>
    </button>
  );
};

// ============================================================================
// Message bubble
// ============================================================================
const MessageBubble = ({ msg }) => {
  if (msg.direction === 'system') {
    return (
      <div className="my-3 flex justify-center" data-testid={`msg-system-${msg.id}`}>
        <div className="inline-flex items-center gap-1.5 text-[10px] text-zinc-500 bg-zinc-100 rounded-full px-3 py-1">
          <Info className="w-3 h-3" />
          <span>{msg.body}</span>
          <span className="text-zinc-400">· {fmtTime(msg.created_at)}</span>
        </div>
      </div>
    );
  }

  const inbound = msg.direction === 'inbound';
  const fromLeadForm = msg.source === 'lead_form';

  return (
    <div
      className={`my-2 flex ${inbound ? 'justify-start' : 'justify-end'}`}
      data-testid={`msg-${msg.direction}-${msg.id}`}
    >
      <div className={`max-w-[85%] min-w-0 rounded-2xl px-3.5 py-2 shadow-sm ${
        inbound ? 'bg-white border border-zinc-200 text-zinc-900 rounded-tl-md' : 'bg-[#F56F05] text-white rounded-tr-md'
      }`}>
        {fromLeadForm && (
          <div className={`text-[9px] uppercase tracking-wider font-bold mb-1 ${
            inbound ? 'text-amber-700' : 'text-orange-100'
          }`}>
            Lead form submission
          </div>
        )}
        <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed">{msg.body}</p>
        <p className={`text-[10px] mt-1 flex items-center gap-1.5 ${inbound ? 'text-zinc-400' : 'text-orange-100/90'}`}>
          {fmtTime(msg.created_at)}
          {!inbound && msg.delivery_status && (
            <span data-testid={`msg-status-${msg.id}`}>
              · {({sending:'Sending…',sent:'Sent',delivered:'Delivered ✓',failed:'Failed',undelivered:'Undelivered'})[msg.delivery_status] || msg.delivery_status}
            </span>
          )}
          {!inbound && msg.user_error && (
            <span className="text-red-100/90" data-testid={`msg-error-${msg.id}`}>· {msg.user_error}</span>
          )}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// Customer context panel
// ============================================================================
const CustomerContextPanel = ({ conv, state, onClose, isMobile }) => {
  const d = conv?.customer_display || {};
  const isLead = (conv?.customer_id || '').startsWith('lead:');
  const smsMeta = STATE_META[state?.sms?.current_status || 'unknown'];
  const emailMeta = STATE_META[state?.email?.current_status || 'unknown'];
  const SmsIcon = smsMeta.icon;
  const EmailIcon = emailMeta.icon;
  const navigate = useNavigate();

  const goToProfile = () => {
    if (isLead) {
      const email = conv.customer_id.replace('lead:', '');
      navigate(`/admin/quote-requests?search=${encodeURIComponent(email)}`);
    } else {
      navigate(`/admin/customers/${conv.customer_id}`);
    }
  };

  return (
    <aside
      data-testid="customer-context-panel"
      className={`bg-white border-l border-zinc-100 ${isMobile ? 'w-full' : 'w-72 flex-shrink-0'} flex flex-col`}
    >
      {isMobile && (
        <div className="border-b border-zinc-100 px-4 h-12 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="context-back-mobile">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <p className="text-sm font-semibold">Customer</p>
        </div>
      )}
      <div className="p-5 flex flex-col items-center border-b border-zinc-100">
        <div className="w-14 h-14 rounded-full bg-zinc-900 text-white text-lg font-bold flex items-center justify-center mb-2">
          {initialsFrom(d.name)}
        </div>
        <p className="text-sm font-bold text-zinc-900 text-center">{d.name || 'Unknown'}</p>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          {isLead ? 'Lead / pre-conversion' : 'Customer'}
        </p>
      </div>
      <div className="p-5 space-y-3 flex-1 overflow-y-auto">
        <ContextRow icon={Mail}  label="Email" value={d.email} />
        <ContextRow icon={Phone} label="Phone" value={d.phone} />
        <div className="pt-2 border-t border-zinc-100">
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-bold mb-2">
            Consent (Module 8)
          </p>
          <ConsentRow icon={SmsIcon}   channel="SMS"   meta={smsMeta}   date={state?.sms?.effective_at} />
          <ConsentRow icon={EmailIcon} channel="Email" meta={emailMeta} date={state?.email?.effective_at} />
          <p className="text-[10px] text-zinc-400 italic mt-2 leading-snug">
            Preference on file never implies consent. Sending is deferred until the provider phase.
          </p>
        </div>
      </div>
      <div className="p-4 border-t border-zinc-100">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={goToProfile}
          data-testid="open-customer-profile-btn"
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
          Open Customer Profile
        </Button>
      </div>
    </aside>
  );
};

const ContextRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-3.5 h-3.5 text-zinc-400 mt-0.5" />
    <div className="min-w-0">
      <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-bold">{label}</p>
      <p className="text-[12px] text-zinc-800 truncate">{value || <span className="text-zinc-400 italic">—</span>}</p>
    </div>
  </div>
);

const ConsentRow = ({ icon: Icon, channel, meta, date }) => (
  <div className="flex items-center justify-between text-[11px] py-1">
    <span className="text-zinc-600">{channel}</span>
    <span className={`inline-flex items-center gap-1 font-medium ${meta.tint}`} data-testid={`consent-${channel.toLowerCase()}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  </div>
);

const DraftOnly = ({ draft, setDraft, onAI, drafting, note }) => (
  <div className="space-y-2" data-testid="composer-draft-only">
    <p className="flex items-start gap-2 text-[11px] text-zinc-500"><Info className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />{note}</p>
    <div className="flex items-end gap-2">
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={draft.length > 160 ? 6 : 2} maxLength={1600} placeholder="Draft a reply…" className="flex-1 min-w-0 resize-none border border-zinc-200 rounded-lg px-3 py-2 text-sm" data-testid="composer-draft-input" />
      <Button variant="outline" onClick={onAI} disabled={drafting} className="h-9 px-3 flex-shrink-0" data-testid="composer-ai-draft-btn">{drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Draft with AI'}</Button>
      <Button variant="outline" disabled={!draft.trim()} onClick={() => { navigator.clipboard?.writeText(draft); toast.success('Draft copied'); }} className="h-9 px-3 flex-shrink-0" data-testid="composer-copy-draft-btn">Copy</Button>
    </div>
  </div>
);

// ============================================================================
// Conversation view
// ============================================================================
const ConversationView = ({ conv, messages, loading, onBack, onChangeStatus, onMarkRead, isMobile, onOpenContext, onSent, providerConfigured, token }) => {
  const ChanIcon = CHANNEL_META[conv?.channel]?.icon || MessageSquare;
  const chanTint = CHANNEL_META[conv?.channel]?.tint || 'text-zinc-500';
  const [savingStatus, setSavingStatus] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const draftWithAI = async () => {
    setDrafting(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/briefing/draft-reply`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ conversation_id: conv.id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(j.detail || 'Could not draft a reply'); return; }
      setDraft(j.draft); toast.success('Draft ready — review and edit before sending');
    } finally { setDrafting(false); }
  };
  const isLead = (conv?.customer_id || '').startsWith('lead:');
  const phone = conv?.customer_display?.phone || '';
  const portal = conv?.channel === 'portal' || conv?.channel === 'team';
  const canCompose = portal || (conv?.channel === 'sms' && !!phone && providerConfigured);

  const sendSms = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    // Stable Idempotency-Key per user click — regen only when input clears.
    const idem = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    try {
      const r = await fetch(`${API_URL}/api/admin/conversations/${conv.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Idempotency-Key': idem,
        },
        body: JSON.stringify({ body: text }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(j.detail || 'Failed to send'); return; }
      setDraft('');
      onSent?.();
    } catch (e) {
      toast.error('Network error — message not sent.');
    } finally { setSending(false); }
  };

  const toggleStatus = async () => {
    if (!conv) return;
    setSavingStatus(true);
    try {
      await onChangeStatus(conv.status === 'open' ? 'closed' : 'open');
    } finally { setSavingStatus(false); }
  };

  useEffect(() => {
    if (conv && (conv.unread_count || 0) > 0) {
      // Auto-mark read on open (fire-and-forget).
      onMarkRead?.(conv.id);
    }
  }, [conv?.id]);

  if (!conv) {
    return (
      <section className="flex-1 flex items-center justify-center bg-zinc-50" data-testid="conversation-empty">
        <div className="text-center max-w-sm px-6">
          <Inbox className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
          <p className="text-sm font-semibold text-zinc-700">Select a conversation</p>
          <p className="text-[12px] text-zinc-500 mt-1">
            Choose a conversation from the inbox to view its history and customer context.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex-1 flex flex-col bg-zinc-50 min-w-0 overflow-x-hidden" data-testid="conversation-view">
      {/* Sticky header */}
      <header className="bg-white border-b border-zinc-100 px-4 h-14 flex items-center gap-2 sticky top-0 z-10">
        {isMobile && (
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="conv-back-mobile">
            <ChevronLeft className="w-4 h-4 mr-1" /> Inbox
          </Button>
        )}
        <div className="w-8 h-8 rounded-full bg-zinc-100 text-zinc-600 flex items-center justify-center text-[11px] font-bold flex-shrink-0">
          {initialsFrom(conv.customer_display?.name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-zinc-900 truncate flex items-center gap-1.5">
            {conv.customer_display?.name || conv.customer_display?.email || 'Unknown'}
            {isLead && (
              <span className="text-[9px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded uppercase tracking-wide">Lead</span>
            )}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500">
              <ChanIcon className={`w-3 h-3 ${chanTint}`} />
              {CHANNEL_META[conv.channel]?.label || conv.channel}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500">
              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_META[conv.status]?.dot}`} />
              {STATUS_META[conv.status]?.label}
            </span>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={toggleStatus} disabled={savingStatus} data-testid="toggle-status-btn" className="flex-shrink-0">
          {savingStatus ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCheck className="w-3 h-3 mr-1" />}
          {conv.status === 'open' ? 'Close' : 'Reopen'}
        </Button>
        {isMobile && (
          <Button variant="ghost" size="sm" onClick={onOpenContext} data-testid="open-context-mobile" aria-label="Customer details" className="flex-shrink-0">
            <User className="w-4 h-4" />
          </Button>
        )}
      </header>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 motion-safe:animate-in motion-safe:fade-in duration-300" data-testid="message-timeline">
        {loading ? (
          <div className="text-center py-16 text-zinc-400"><Loader2 className="w-5 h-5 inline animate-spin mr-2" />Loading messages…</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-16 text-sm text-zinc-500 italic">No messages yet.</div>
        ) : (
          messages.map(m => <MessageBubble key={m.id} msg={m} />)
        )}
      </div>

      {/* Composer — active for SMS conversations when provider is configured */}
      {conv?.channel === 'sms' || portal ? (
        <div className="bg-white border-t border-zinc-100 px-3 py-2.5" data-testid="sms-composer">
          {!providerConfigured && !portal ? (
            <DraftOnly draft={draft} setDraft={setDraft} onAI={draftWithAI} drafting={drafting} note="SMS sending is not connected yet (Quo). You can still prepare a reply and copy it." />
          ) : !phone && !portal ? (
            <div className="flex items-start gap-2 text-[11px] text-zinc-500" data-testid="composer-no-phone">
              <Info className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
              No phone number on file for this contact. Add one to enable sending.
            </div>
          ) : (
            <div className="flex items-end gap-2 w-full min-w-0">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendSms(); } }}
                placeholder={conv?.channel === 'team' ? 'Reply in the Guru team thread…' : portal ? 'Reply in the client portal…' : `Reply to ${phone}…`}
                maxLength={1600}
                rows={2}
                className="flex-1 min-w-0 w-full resize-none border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#F56F05]"
                data-testid="composer-input"
                disabled={sending}
              />
              <Button variant="outline" onClick={draftWithAI} disabled={drafting || sending} className="h-9 px-3 flex-shrink-0" data-testid="composer-ai-draft-btn" title="Uses the reply style saved in AI Workspace → Preferences">
                {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Draft with AI'}
              </Button>
              <Button
                onClick={sendSms}
                disabled={sending || !draft.trim()}
                className="bg-[#F56F05] hover:bg-orange-600 h-9 px-4 flex-shrink-0"
                data-testid="composer-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border-t border-zinc-100 px-3 py-2.5" data-testid="composer-disabled-notice">
          <DraftOnly draft={draft} setDraft={setDraft} onAI={draftWithAI} drafting={drafting} note="Sending on this channel is not yet enabled. History is captured for reference; prepare a reply here and copy it into your email." />
        </div>
      )}
    </section>
  );
};

const InboxOverview = ({ token }) => {
  const [c, setC] = useState({});
  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API_URL}/api/admin/conversations/summary`, h).then((r) => setC((p) => ({ ...p, unread: r.data?.unread_conversations ?? 0 }))).catch(() => {});
    axios.get(`${API_URL}/api/admin/inquiries/stats`, h).then((r) => setC((p) => ({ ...p, inquiries: r.data?.new ?? 0 }))).catch(() => {});
    axios.get(`${API_URL}/api/admin/emails/failed`, h).then((r) => setC((p) => ({ ...p, emails: r.data?.length ?? 0 }))).catch(() => {});
  }, [token]);
  const items = [['unread', 'Unread conversations', '/admin/communication'], ['inquiries', 'New general inquiries', '/admin/inquiries'], ['emails', 'Email issues to review', '/admin/emails/failed']];
  return (
    <div className="grid grid-cols-3 mt-5 rounded-[9px] border border-[#e7e4db] bg-[#faf9f6] overflow-hidden" data-testid="inbox-overview">
      {items.map(([k, l, to]) => (
        <a key={k} href={to} className="p-4 sm:p-5 border-r border-[#e7e4db] last:border-r-0 hover:bg-[#eeeae0]" data-testid={`inbox-overview-${k}`}>
          <strong className="block text-2xl sm:text-3xl font-medium text-[#20211d]">{c[k] ?? '…'}</strong><span className="text-[11px] text-[#77776e]">{l}</span>
        </a>
      ))}
    </div>
  );
};

// ============================================================================
// Main page
// ============================================================================
const AdminCommunicationPage = () => {
  const { token } = useAuth();
  const [providerConfigured, setProviderConfigured] = useState(false);
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/admin/conversations/provider-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(j => setProviderConfigured(!!j?.adapters?.sms?.configured))
      .catch(() => {});
  }, [token]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialId = searchParams.get('id') || searchParams.get('conversation') || null;
  const initialCustomer = searchParams.get('customer') || null;
  const initialChannel = searchParams.get('channel') || 'sms';

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(initialId);
  const [detail, setDetail] = useState({ conversation: null, messages: [], communication_state: {} });
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [providerStatus, setProviderStatus] = useState(null);
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'thread' | 'context'

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (readFilter !== 'all')   params.read = readFilter;
      if (search.trim())          params.search = search.trim();
      const r = await axios.get(`${API_URL}/api/admin/conversations`, { headers, params });
      setConversations(r.data?.conversations || []);
    } catch (err) {
      toast.error('Failed to load conversations');
      setConversations([]);
    } finally { setLoading(false); }
  }, [headers, statusFilter, readFilter, search]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) { setDetail({ conversation: null, messages: [], communication_state: {} }); return; }
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/conversations/${id}`, { headers });
      setDetail(r.data);
    } catch (err) {
      if (err.response?.status === 404) toast.error('Conversation not found');
      else toast.error('Failed to load conversation');
      setDetail({ conversation: null, messages: [], communication_state: {} });
    } finally { setDetailLoading(false); }
  }, [headers]);

  const fetchProviderStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/conversations/provider-status`, { headers });
      setProviderStatus(r.data?.adapters || null);
    } catch { /* silent */ }
  }, [headers]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);
  useEffect(() => { fetchProviderStatus(); }, [fetchProviderStatus]);

  // Deep-link: `?id=` or `?customer=&channel=` opens the conversation.
  useEffect(() => {
    (async () => {
      if (initialId) { setSelectedId(initialId); setMobileView('thread'); return; }
      if (initialCustomer) {
        try {
          const r = await axios.get(
            `${API_URL}/api/admin/conversations/customer/${encodeURIComponent(initialCustomer)}/open?channel=${initialChannel}`,
            { headers }
          );
          if (r.data?.conversation?.id) {
            setSelectedId(r.data.conversation.id);
            setMobileView('thread');
          }
        } catch {
          toast.info('No conversation yet for this customer.');
        }
      }
    })();
     
  }, []);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
    else setDetail({ conversation: null, messages: [], communication_state: {} });
  }, [selectedId, fetchDetail]);

  const handleSelect = (conv) => {
    setSelectedId(conv.id);
    setMobileView('thread');
    setSearchParams({ id: conv.id });
  };

  const handleBackToList = () => {
    setMobileView('list');
    setSearchParams({});
  };

  const handleChangeStatus = async (status) => {
    if (!selectedId) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/conversations/${selectedId}/status`,
        { status }, { headers }
      );
      toast.success(status === 'closed' ? 'Conversation closed' : 'Conversation reopened');
      // Re-fetch the full detail so the new system message appears in the timeline.
      await fetchDetail(selectedId);
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/conversations/${id}/read`, {}, { headers });
      // Optimistic — clear unread on the row in the inbox list.
      setConversations(prev => prev.map(c => c.id === id ? { ...c, unread_count: 0 } : c));
    } catch { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-[#f4f2ed]" data-testid="admin-communication-page">
      <InboxTabs />
      <section className="px-5 sm:px-9 pt-6 pb-4 bg-[#f4f2ed]">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-[-0.04em] text-[#20211d]">Inbox<span className="text-[#939681] font-normal">.</span></h1>
        <p className="text-sm text-[#77776e] mt-2">Every conversation has an owner. Every follow-up has a next step.</p>
        <InboxOverview token={token} />
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <p className="text-[11px] text-[#77776e] flex-1 min-w-[220px]">Client, lead and team conversations. Drafts stay here until you send them.</p>
          {providerStatus && (
            <div className="flex items-center gap-3 text-[11px] text-[#5f6455]" data-testid="provider-status-badges">
              {Object.entries(providerStatus).map(([ch, s]) => (
                <span key={ch} className="inline-flex items-center gap-1"><CircleDot className={`w-3 h-3 ${s.configured ? 'text-emerald-500' : 'text-zinc-300'}`} />{ch.toUpperCase()} {s.configured ? 'connected' : 'not connected'}</span>
              ))}
            </div>
          )}
          <Button size="sm" variant="outline" onClick={fetchInbox} disabled={loading} data-testid="refresh-inbox-btn"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
        </div>
      </section>

      <main className="mx-5 sm:mx-9 mb-8 flex rounded-[10px] border border-[#e7e4db] overflow-hidden bg-white h-[calc(100vh-150px)] md:h-[calc(100vh-300px)] min-h-[540px]" data-testid="conversations-panel">
        {/* ==================== INBOX (LIST) ==================== */}
        <aside
          data-testid="inbox-list"
          className={`bg-white md:border-r md:border-zinc-100 md:w-96 md:flex-shrink-0 flex-col min-w-0 overflow-x-hidden ${
            mobileView === 'list' ? 'flex flex-1' : 'hidden md:flex'
          }`}
        >
          {/* Filters */}
          <div className="p-3 border-b border-zinc-100 space-y-2 sticky top-0 bg-white z-10">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search name, phone, email"
                className="pl-8 h-9 text-sm"
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="inbox-search"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={readFilter} onValueChange={setReadFilter}>
                <SelectTrigger className="h-8 text-xs flex-1" data-testid="filter-read">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="unread">Unread</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Rows */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="py-16 text-center text-zinc-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading inbox…</div>
            ) : conversations.length === 0 ? (
              <div className="py-16 text-center text-sm text-zinc-500 px-6" data-testid="inbox-empty">
                <Inbox className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="font-semibold text-zinc-700">You&apos;re all caught up.</p>
                <p className="text-[12px] mt-1">New conversations will appear here when leads submit inquiries or when future provider integrations deliver messages.</p>
              </div>
            ) : (
              conversations.map(c => (
                <InboxRow key={c.id} conv={c}
                  selected={c.id === selectedId}
                  onSelect={handleSelect}
                />
              ))
            )}
          </div>
        </aside>

        {/* ==================== CONVERSATION (THREAD) ==================== */}
        <div className={`${mobileView === 'thread' ? 'flex flex-1' : 'hidden md:flex md:flex-1'} min-w-0`}>
          <ConversationView
            conv={detail.conversation}
            messages={detail.messages || []}
            loading={detailLoading}
            onBack={handleBackToList}
            onChangeStatus={handleChangeStatus}
            onMarkRead={handleMarkRead}
            isMobile={mobileView === 'thread'}
            onOpenContext={() => setMobileView('context')}
            onSent={() => selectedId && fetchDetail(selectedId)}
            providerConfigured={providerConfigured}
            token={token}
          />
        </div>

        {/* ==================== CUSTOMER CONTEXT (RIGHT) ==================== */}
        {detail.conversation && (
          <div className={`${mobileView === 'context' ? 'flex flex-1' : 'hidden lg:flex'}`}>
            <CustomerContextPanel
              conv={detail.conversation}
              state={detail.communication_state}
              onClose={() => setMobileView('thread')}
              isMobile={mobileView === 'context'}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminCommunicationPage;
